import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  CalendarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import DashboardLayout from '../components/DashboardLayout';
import { patientsService } from '../services/api';

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPatient();
    loadPatientExams();
  }, [id]);

  const loadPatient = async () => {
    try {
      const data = await patientsService.getById(id);
      setPatient(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientExams = async () => {
    try {
      const data = await patientsService.getExams(id);
      setExams(data);
    } catch (err) {
      console.error('Erro ao carregar exames:', err);
      setExams([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatCRM = (crm, crmState) => {
    if (!crm) return null;
    return crmState ? `${crm}-${crmState}` : crm;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
            <button
              onClick={() => navigate('/patients')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              ← Voltar para lista de pacientes
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/patients')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Voltar
          </button>
          <button
            onClick={() => navigate(`/patients/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PencilSquareIcon className="w-5 h-5" />
            Editar Paciente
          </button>
        </div>

        {/* Patient Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{patient.name}</h1>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Idade</label>
              <p className="text-lg font-semibold text-gray-900">{patient.age} anos</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Sexo</label>
              <p className="text-lg font-semibold text-gray-900">{patient.sex}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Cadastrado em</label>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(patient.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>

          {/* Dados Ocupacionais (se disponíveis) */}
          {(patient.company || patient.jobTitle) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-200">
              {patient.company && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Empresa</label>
                  <p className="text-lg font-semibold text-gray-900">{patient.company}</p>
                </div>
              )}
              {patient.jobTitle && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Cargo/Função</label>
                  <p className="text-lg font-semibold text-gray-900">{patient.jobTitle}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/report-wizard"
              state={{ patientId: patient.id }}
              className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <DocumentTextIcon className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-semibold text-gray-900">Organizar Laudo</p>
                <p className="text-sm text-gray-600">Iniciar organização de laudo</p>
              </div>
            </Link>

            <button
              onClick={() => toast.info('Funcionalidade em desenvolvimento', {
                icon: '🚧',
                duration: 3000,
              })}
              className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <DocumentTextIcon className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-semibold text-gray-900">Novo Exame</p>
                <p className="text-sm text-gray-600">Cadastrar novo exame audiométrico</p>
              </div>
            </button>
          </div>
        </div>

        {/* Patient Exams */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Histórico de Exames</h2>
          
          {exams.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DocumentTextIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Nenhum exame cadastrado para este paciente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Operador
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Registro
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {exams.map((exam) => (
                    <tr key={exam.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{formatDate(exam.date)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{exam.operatorName || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 font-medium">
                          {formatCRM(exam.operatorCrm, exam.operatorCrmState) || exam.operatorRegistration || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/exams/${exam.id}`}
                          title="Visualizar detalhes"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <EyeIcon className="w-4 h-4" />
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PatientDetail;
